import { Link } from "@tanstack/react-router";
import { Phone, CheckCircle2, AlertTriangle } from "lucide-react";
import { getBusinessConfig } from "@/lib/site-config";
import { AnimatedSection } from "@/components/AnimatedSection";

const ITEMS: { label: string; note: string }[] = [
  { label: "Lights", note: "Headlights, brakes, indicators, fogs, plate lights — walk round with someone or use a wall reflection." },
  { label: "Tyres", note: "1.6mm tread minimum across the central three quarters. Check sidewalls for cuts and bulges." },
  { label: "Wipers & washers", note: "No streaks or judder. Top washer bottle to MAX — an empty bottle is an automatic fail." },
  { label: "Horn", note: "Must be clearly audible. Cheap to swap if it isn't working." },
  { label: "Mirrors", note: "All present, intact and adjustable. Driver's door mirror is required." },
  { label: "Number plates", note: "Both secure, legal font, unfaded and clean enough to read." },
  { label: "Warning lights", note: "Engine management, ABS, airbag and brake-system lights are all an automatic major in most cases." },
  { label: "Seatbelts", note: "Retract smoothly and lock with a sharp tug. Replacement webbing is an MOT requirement." },
  { label: "Fluid leaks", note: "Quick look under the car after standing overnight. Fresh drips need investigating first." },
  { label: "Dashboard messages", note: "AdBlue empty, service-due or 'limp mode' messages can hide bigger issues — get them checked." },
];

/**
 * Reusable "before your MOT" prep checklist — practical, plain-English checks
 * a driver can do in 10 minutes. Drops into MOT-related pages without bringing
 * its own FAQ schema (host page already emits FAQPage where needed).
 */
export function MOTPrepChecklistBlock({
  variant = "default",
  showCta = true,
}: {
  variant?: "default" | "compact";
  showCta?: boolean;
}) {
  const biz = getBusinessConfig();
  const isCompact = variant === "compact";

  return (
    <AnimatedSection>
      <section
        aria-labelledby="mot-prep-checklist-heading"
        className={`${isCompact ? "py-10" : "py-14 md:py-20"} bg-card`}
      >
        <div className="mx-auto max-w-4xl px-4 md:px-6">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-primary">Before your MOT</p>
            <h2 id="mot-prep-checklist-heading" className="mt-3 text-2xl md:text-3xl font-bold text-foreground tracking-tight">
              Quick checks that can save you a retest
            </h2>
            <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed">
              Ten things any driver can check in about ten minutes. They're the issues we see fail the most often at our DVSA-authorised station — and they're almost all cheap to put right at home.
            </p>
          </div>

          <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {ITEMS.map((item) => (
              <li
                key={item.label}
                className="flex items-start gap-3 rounded-xl border border-border bg-background/60 p-4"
              >
                <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-bold text-foreground">{item.label}</p>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{item.note}</p>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-6 flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400 max-w-2xl mx-auto">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              Got a dashboard warning light? Sort it before booking — most are an automatic MOT fail. We can scan and quote first.
            </span>
          </div>

          {showCta && (
            <div className="mt-8 text-center">
              <p className="text-sm text-muted-foreground max-w-xl mx-auto">
                Unsure about anything on the list? Call Woodseer before you come in — a 60-second chat usually saves time, money and a wasted trip.
              </p>
              <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                <a
                  href={biz.phoneHref}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  <Phone className="h-4 w-4" />
                  Call {biz.phone}
                </a>
                <Link
                  to="/book"
                  search={{ service: "mot" }}
                  className="inline-flex items-center rounded-lg border border-border bg-background px-5 py-3 text-sm font-bold text-foreground hover:border-primary/40 transition-colors"
                >
                  Request MOT booking
                </Link>
                <Link
                  to="/mot"
                  className="inline-flex items-center rounded-lg border border-border bg-background px-5 py-3 text-sm font-bold text-foreground hover:border-primary/40 transition-colors"
                >
                  MOT info
                </Link>
              </div>
              <div className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                <Link to="/guide/$slug" params={{ slug: "mot-warning-lights-before-test" }} className="hover:text-primary">
                  Warning lights guide
                </Link>
                <span aria-hidden="true">·</span>
                <Link to="/guide/$slug" params={{ slug: "tyre-check-before-mot" }} className="hover:text-primary">
                  Tyre check guide
                </Link>
                <span aria-hidden="true">·</span>
                <Link to="/blog/$slug" params={{ slug: "mot-failure-reasons-how-to-avoid-them" }} className="hover:text-primary">
                  Top MOT failures
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </AnimatedSection>
  );
}
